import { zipSync, type Zippable } from 'fflate'

function slugify(value: string): string {
  // Drop combining accent marks by code point, then keep a filesystem-safe slug.
  return Array.from(value.trim().toLowerCase().normalize('NFD'))
    .filter((character) => {
      const code = character.codePointAt(0) ?? 0
      return code < 0x300 || code > 0x36f
    })
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

/** e.g. "Asamblea de Marzo" photo 3 -> "asamblea-de-marzo-03.webp". */
export function photoFileName(eventTitle: string, index: number, storagePath: string): string {
  const extension = storagePath.toLowerCase().endsWith('.jpg') ? 'jpg' : 'webp'
  return `${slugify(eventTitle) || 'actividad'}-${String(index + 1).padStart(2, '0')}.${extension}`
}

export function photosZipName(eventTitle: string): string {
  return `${slugify(eventTitle) || 'actividad'}-fotos.zip`
}

/**
 * Builds the zip in the browser. Photos are already WebP/JPEG, so they are stored without
 * recompressing (level 0): deflating them again costs CPU and saves almost nothing.
 */
export async function buildPhotosZip(
  files: readonly { readonly name: string; readonly url: string }[],
  onProgress?: (done: number, total: number) => void,
): Promise<Blob> {
  const entries: Zippable = {}
  for (const [index, file] of files.entries()) {
    const response = await fetch(file.url)
    if (!response.ok) throw new Error(`No se pudo descargar ${file.name}.`)
    entries[file.name] = [new Uint8Array(await response.arrayBuffer()), { level: 0 }]
    onProgress?.(index + 1, files.length)
  }
  const zipped = zipSync(entries)
  return new Blob([zipped as unknown as BlobPart], { type: 'application/zip' })
}

export function triggerDownload(href: string, fileName?: string): void {
  const anchor = document.createElement('a')
  anchor.href = href
  if (fileName) anchor.download = fileName
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  triggerDownload(url, fileName)
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}
